import { DateTime } from "luxon";
import { Organization } from "../models/interfaces/organization";
import { User } from "../models/interfaces/user";

interface hubspotUserDataRow {
    firstName: string,
    lastName: string,
    emailAddress: string,
    lastLoggedIn: string,
    symmioRole: string,
    orgName: string,
    orgIndustry: string,
    orgUserCount: number,
    orgLicenseCount: number,
    plan: string,
}

export default class DataExportUtil {
    // -----------------------------------------------------------------------------------------
    // #region Public Methods
    // -----------------------------------------------------------------------------------------

    private static getUserRole = (roles: string[] | null | undefined): string => {
        if (roles && roles.length > 0) {
            if (roles[0] !== null && roles[0] !== '') {
                return roles[0];
            }
        }
        return 'none';
    };

    private static capitalizeName = (name: string): string => {
        if (name === '') {
            return '';
        }

        let capped = name.charAt(0).toUpperCase();
        capped += name.substring(1);
        return capped;
    }

    public static getHubspotUserDataRows(users: User[], orgs: Organization[], onlyRoles: boolean): hubspotUserDataRow[] {
        const rows: hubspotUserDataRow[] = [];

        users.forEach((u) => {
            if (u.organizationId && u.email) {
                const org = orgs.find((o) => o.id === u.organizationId);

                if (org?.testingOrg) {
                    return;
                }

                if (org) {
                    const role = this.getUserRole(u.roles);
                    const hubspotUser: hubspotUserDataRow = {
                        firstName: this.capitalizeName(u.firstName ?? ''),
                        lastName: this.capitalizeName(u.lastName ?? ''),
                        emailAddress: u.email,
                        lastLoggedIn: u.lastLoggedIn
                            ? (() => {
                                const daysDifference = Math.floor(DateTime.now().diff(DateTime.fromISO(u.lastLoggedIn), ["days"]).days);
                                return daysDifference === 1
                                    ? `${daysDifference} day ago`
                                    : `${daysDifference} days ago`;
                            })()
                            : "",
                        symmioRole: this.getUserRole(u.roles),
                        orgName: org.name ?? '',
                        orgIndustry: (org.industry && org.industry.label) ? org.industry.label : '',
                        orgUserCount: org.userCount ?? 0,
                        orgLicenseCount: org.mySymmioLicenseCount ?? 0,
                        plan: org.plan?.label ?? "",
                    }
                    if (onlyRoles) {
                        if (role !== 'none') {
                            rows.push(hubspotUser);
                        }
                    } else {
                        if (role === 'none') {
                            rows.push(hubspotUser);
                        }
                    }
                }
            }
        });

        return rows;
    };

    public static generateHubspotUserCsv(rows: hubspotUserDataRow[]): string {
        let csv = '';

        const HEADERS = 'firstName,lastName,emailAddress,lastLoggedIn,symmioRole,orgName,orgIndustry,orgUserCount,orgLicenseCount,plan\n';

        csv += HEADERS;

        rows.forEach((r) => {
            let row = '';
            row += this.escapeCsvValue(r.firstName) + ',';
            row += this.escapeCsvValue(r.lastName) + ',';
            row += this.escapeCsvValue(r.emailAddress) + ',';
            row += this.escapeCsvValue(r.lastLoggedIn) + ',';
            row += this.escapeCsvValue(r.symmioRole) + ',';
            row += this.escapeCsvValue(r.orgName) + ',';
            row += this.escapeCsvValue(r.orgIndustry) + ',';
            row += r.orgUserCount + ','; // Numbers don't need escaping
            row += r.orgLicenseCount + ','; // Numbers don't need escaping
            row += this.escapeCsvValue(r.plan) + '\n';
            csv += row;
        });

        return csv;
    }

    // Escapes values that might contain commas, quotes, or newlines
    private static escapeCsvValue(value: string): string {
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            return `"${value.replace(/"/g, '""')}"`; // Escape double quotes by doubling them
        }
        return value;
    }

    // #endregion Public Methods
}
